import {
  API,
  PolicyItemVisibilityChoiceDTO,
  PolicyWorksheetViewDTO,
  WorksheetAnswerInheritanceDTO,
  WorksheetItemViewDTO
} from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { LocationVarInheritance, MergedLocationVar, PolicyWorksheetMode, URLS } from "@swivl-mono/types";
import { BasicSelect, Button, ButtonColor, ButtonSize, ButtonVariant, Checkbox, Input, SearchInput, TextArea, Tooltip } from "@swivl-mono/ui";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { GrRevert } from "react-icons/gr";
import { TbChevronDown } from "react-icons/tb";
import { useParams, useSearchParams } from "react-router-dom";

import "../../../Components/SidebarLink/SidebarLink.scss";
import { useMergedLocationVariables } from "../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { CortexTopicWorksheet } from "../../../Worksheets/DetailsV3/Components/CortexTopicWorksheet";
import { CortexInheritancePill } from "../CortexV3/Components/CortexInheritancePill";
import { CortexRowShell } from "../CortexV3/Components/CortexRowShell";
import { revertLabelFor } from "../CortexV3/Components/revertTarget";
import { PolicyHint } from "./PolicyHint";
import { PolicyRadio } from "./PolicyRadio";
import { PolicyToggleRow } from "./PolicyToggleRow";

/**
 * Policy Worksheets — the operator-facing worksheet screens (Policy Builder,
 * design doc §9). Mirrors the Cortex V3 interaction grammar: a left rail of
 * worksheet groups, one worksheet per screen, Cortex-style rows (pill +
 * Customize / Revert / Discard) and a sticky save bar when dirty. Operators
 * answer inputs, never see generated text (D3); Swivl staff get a collapsed
 * "Generated text" panel. Gated by FeatureFlagKey.PolicyBuilder at the
 * route/tab (Router.tsx).
 */

const INHERITANCE_TO_ENUM: Record<string, LocationVarInheritance> = {
  none: LocationVarInheritance.NONE,
  group: LocationVarInheritance.GROUP,
  org: LocationVarInheritance.ORG,
  default: LocationVarInheritance.DEFAULT
};

/** The parent scope's effective answer for one item: what a revert here lands on. */
interface ParentAnswer {
  value: string;
  inheritance: LocationVarInheritance;
}

/**
 * ENG-3357 — the Swivl-staff "Custom instructions" entry at the bottom of the
 * Policies list. Not a worksheet: it renders the `custom-instructions` Cortex
 * topic (one bounded free-text var + the Policy Assistant) in the main pane.
 */
const CUSTOM_INSTRUCTIONS_KEY = "custom-instructions";

interface PolicyTopicSettings {
  /** Cortex catalog topic id (TOPIC_COMPONENTS). */
  topicId: string;
  /** Where the section renders relative to the worksheet items. Defaults to "bottom". */
  position?: "top" | "bottom";
  /** Persist edits automatically (no Save button). Only safe for structured-var topics, not LLM-formatted policy topics. */
  autoSave?: boolean;
  /** The topic renders its own Edit / Cancel + Save in its card header. */
  sectionEditing?: boolean;
  /** Render only while this worksheet answer matches (same semantics as item showIf). */
  showIf?: { itemKey: string; equals: string };
}

/**
 * Typed settings (location vars) that belong to a policy topic and render as a
 * section at the bottom of that policy's worksheet. They are NOT worksheet
 * answers — they keep their own var save path (CortexTopicWorksheet) — they
 * just live on the same screen as the policy they configure. Moved here from
 * the Details v3 rail (Chuck, 2026-07-24: these are policies, not details).
 */
const POLICY_TOPIC_SETTINGS: Record<string, PolicyTopicSettings> = {
  "gate-code": {
    topicId: "gate-code",
    position: "top",
    autoSave: true
  },
  "move-out": {
    topicId: "move-out",
    autoSave: true,
    showIf: { itemKey: "moveout-request-method", equals: "form" }
  },
  "profanity-handling": {
    topicId: "profanity-handling",
    position: "top",
    sectionEditing: true
  },
  reservation: {
    topicId: "lead-creation",
    sectionEditing: true
  }
};

/** The keys explicitly set at the requested scope (inheritance === none). */
function ownAnswers(worksheet: PolicyWorksheetViewDTO): Record<string, string> {
  const own: Record<string, string> = {};
  for (const [itemKey, answer] of Object.entries(worksheet.answers)) {
    if (answer.inheritance === "none") {
      own[itemKey] = answer.value;
    }
  }
  return own;
}

/** Item keys whose draft differs from what is saved at this scope. */
function dirtyItemKeys(worksheet: PolicyWorksheetViewDTO, draft: Record<string, string>): string[] {
  const own = ownAnswers(worksheet);
  const keys = new Set([...Object.keys(own), ...Object.keys(draft)]);
  return [...keys].filter((itemKey) => own[itemKey] !== draft[itemKey]);
}

/** A `linksToUrlKey` hint's content — mirrors the Amenities row hint. */
function buildUrlLink(
  urlKey: string | undefined,
  urlVars: Record<string, MergedLocationVar> | undefined,
  href: string
): { href: string; body: string; linkLabel: string } | undefined {
  const urlDef = URLS.find((u) => u.key === urlKey);
  if (!urlDef) return undefined;
  const value = (urlVars?.[urlDef.urlVarKey]?.value ?? "").trim();
  return value
    ? { href, body: `Linked to ${value} -`, linkLabel: "Edit in URLs" }
    : { href, body: `Have a ${urlDef.label.toLowerCase()} link?`, linkLabel: "Add it in URLs" };
}

export function PolicyWorksheetsView(): React.ReactElement {
  const { groupId, locationId } = useParams<{ groupId?: string; locationId?: string }>();
  const locationPage = usePropertiesGetLocationIds();
  const urlsHref = `${locationPage.preamble}/page/details-v3/urls`;
  // Current-scope values for every `linksToUrlKey` hint, so it can show "Linked to X"
  // the same way the Amenities row hint does.
  const urlVars = useMergedLocationVariables(
    URLS.flatMap((u) => [u.urlVarKey, u.enabledVarKey]),
    locationPage.locationId ? undefined : locationPage.groupId,
    locationPage.locationId
  );
  // Deep link (?policyKey=...) — review-notification tickets land here. Read
  // once via a ref so later URL updates from rail clicks don't re-run load().
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPolicyKeyRef = useRef(searchParams.get("policyKey"));
  const orgId = Member.orgId;
  const parsedGroupId = groupId ? Number(groupId) : undefined;
  const parsedLocationId = locationId ? Number(locationId) : undefined;
  // Org is the top scope: a revert there lands on the definition default, not a parent.
  const isOrgScope = parsedGroupId === undefined && parsedLocationId === undefined;

  const [worksheets, setWorksheets] = useState<PolicyWorksheetViewDTO[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedTexts, setGeneratedTexts] = useState<Record<string, string> | null>(null);
  const [modeSaving, setModeSaving] = useState<boolean>(false);
  const [modeError, setModeError] = useState<string | undefined>(undefined);
  const [visibilitySaving, setVisibilitySaving] = useState<boolean>(false);
  const [visibilityError, setVisibilityError] = useState<string | undefined>(undefined);
  const [railSearch, setRailSearch] = useState<string>("");
  /** Which worksheets are in edit mode, keyed by policyKey (Details v3 grammar). */
  const [editingKeys, setEditingKeys] = useState<Record<string, boolean>>({});
  /**
   * The parent scope's effective answers, keyed policyKey → itemKey → value, used
   * only for the "Reverts to: …" tooltips. Mirrors Details v3, which loads the
   * parent scope's vars for exactly this reason: a row's own answer is all the
   * DTO carries, so what a revert lands on has to be fetched. Location → its
   * group, group → org; at org scope there is no parent and a revert falls back
   * to the definition default.
   */
  const [parentAnswers, setParentAnswers] = useState<Record<string, Record<string, ParentAnswer>>>({});

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await API.PolicyWorksheets.getWorksheets(orgId, parsedGroupId, parsedLocationId);
      setWorksheets(response.worksheets);
      const initialDrafts: Record<string, Record<string, string>> = {};
      for (const worksheet of response.worksheets) {
        initialDrafts[worksheet.policyKey] = ownAnswers(worksheet);
      }
      setDrafts(initialDrafts);
      setErrors({});
      setActiveKey((previous) => {
        const preferred = previous ?? linkedPolicyKeyRef.current;
        return preferred && response.worksheets.some((worksheet) => worksheet.policyKey === preferred)
          ? preferred
          : (response.worksheets[0]?.policyKey ?? null);
      });
    } catch {
      setErrors({ _load: "Could not load the policy worksheets." });
    } finally {
      setLoading(false);
    }
  }, [orgId, parsedGroupId, parsedLocationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (isOrgScope) {
      setParentAnswers({});
      return;
    }
    const parentGroupId = parsedLocationId !== undefined ? parsedGroupId : undefined;
    let cancelled = false;
    void (async () => {
      try {
        const response = await API.PolicyWorksheets.getWorksheets(orgId, parentGroupId, undefined);
        if (cancelled) return;
        const byPolicy: Record<string, Record<string, ParentAnswer>> = {};
        for (const worksheet of response.worksheets) {
          byPolicy[worksheet.policyKey] = Object.fromEntries(
            Object.entries(worksheet.answers).map(([itemKey, answer]) => [
              itemKey,
              // The parent's own override ("none") is, from this scope, the group's or
              // the org's value — whichever scope the parent fetch actually hit.
              {
                value: answer.value,
                inheritance:
                  answer.inheritance === "none"
                    ? parentGroupId !== undefined
                      ? LocationVarInheritance.GROUP
                      : LocationVarInheritance.ORG
                    : INHERITANCE_TO_ENUM[answer.inheritance]
              }
            ])
          );
        }
        setParentAnswers(byPolicy);
      } catch {
        // Tooltip detail only — never let this break the page.
        if (!cancelled) setParentAnswers({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, parsedGroupId, parsedLocationId, isOrgScope]);

  const groups = useMemo(() => {
    const byLabel = new Map<string, PolicyWorksheetViewDTO[]>();
    for (const worksheet of worksheets) {
      const label = worksheet.groupLabel ?? "General";
      byLabel.set(label, [...(byLabel.get(label) ?? []), worksheet]);
    }
    return [...byLabel.entries()];
  }, [worksheets]);

  /** Rail search: keeps only matching worksheets, dropping groups left empty. */
  const visibleGroups = useMemo<[string, PolicyWorksheetViewDTO[]][]>(() => {
    const query = railSearch.trim().toLowerCase();
    if (query === "") {
      return groups;
    }
    const matched: [string, PolicyWorksheetViewDTO[]][] = [];
    for (const [label, groupWorksheets] of groups) {
      const hits = groupWorksheets.filter((worksheet) => worksheet.title.toLowerCase().includes(query));
      if (hits.length > 0) {
        matched.push([label, hits]);
      }
    }
    return matched;
  }, [groups, railSearch]);

  const dirtyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const worksheet of worksheets) {
      counts[worksheet.policyKey] = dirtyItemKeys(worksheet, drafts[worksheet.policyKey] ?? {}).length;
    }
    return counts;
  }, [worksheets, drafts]);

  const activeWorksheet = useMemo(() => worksheets.find((worksheet) => worksheet.policyKey === activeKey) ?? null, [worksheets, activeKey]);

  const handleAnswerChange = useCallback((policyKey: string, itemKey: string, value: string): void => {
    setDrafts((previous) => ({
      ...previous,
      [policyKey]: { ...previous[policyKey], [itemKey]: value }
    }));
  }, []);

  /** Stage removal of this scope's answer so it inherits again after save. */
  const handleRevertItem = useCallback((policyKey: string, itemKey: string): void => {
    setDrafts((previous) => {
      const next = { ...previous[policyKey] };
      delete next[itemKey];
      return { ...previous, [policyKey]: next };
    });
  }, []);

  /** Discard the staged change, returning the item to its saved state. */
  const handleDiscardItem = useCallback((worksheet: PolicyWorksheetViewDTO, itemKey: string): void => {
    const answer = worksheet.answers[itemKey];
    setDrafts((previous) => {
      const next = { ...previous[worksheet.policyKey] };
      if (answer?.inheritance === "none") {
        next[itemKey] = answer.value;
      } else {
        delete next[itemKey];
      }
      return { ...previous, [worksheet.policyKey]: next };
    });
  }, []);

  /** Cancel: drop every staged edit and leave edit mode (Details v3 `cancelEdits`). */
  const handleCancelEdits = useCallback(
    (worksheet: PolicyWorksheetViewDTO): void => {
      const isDirty = dirtyItemKeys(worksheet, drafts[worksheet.policyKey] ?? {}).length > 0;
      if (isDirty && !window.confirm("Discard all unsaved changes on this worksheet?")) return;
      setDrafts((previous) => ({ ...previous, [worksheet.policyKey]: ownAnswers(worksheet) }));
      setErrors((previous) => {
        if (!previous[worksheet.policyKey]) return previous;
        const { [worksheet.policyKey]: _drop, ...rest } = previous;
        return rest;
      });
      setEditingKeys((previous) => ({ ...previous, [worksheet.policyKey]: false }));
    },
    [drafts]
  );

  const handleSave = useCallback(
    async (worksheet: PolicyWorksheetViewDTO): Promise<void> => {
      setSaving(true);
      try {
        const result = await API.PolicyWorksheets.saveAnswers(orgId, worksheet.policyKey, {
          groupId: parsedGroupId,
          locationId: parsedLocationId,
          answers: drafts[worksheet.policyKey] ?? {},
          revision: worksheet.revision
        });
        if (!result.ok) {
          setErrors((previous) => ({ ...previous, [worksheet.policyKey]: result.error ?? "Save failed." }));
        } else {
          setEditingKeys((previous) => ({ ...previous, [worksheet.policyKey]: false }));
          setGeneratedTexts(null);
          await load();
        }
      } catch {
        setErrors((previous) => ({ ...previous, [worksheet.policyKey]: "Save failed. Please try again." }));
      } finally {
        setSaving(false);
      }
    },
    [orgId, parsedGroupId, parsedLocationId, drafts, load]
  );

  /** Swivl-staff: switch a scope between generated and hand-written custom text (D6). */
  const handleSetMode = useCallback(
    async (worksheet: PolicyWorksheetViewDTO, mode: PolicyWorksheetMode, customText?: string): Promise<void> => {
      setModeSaving(true);
      setModeError(undefined);
      try {
        const result = await API.PolicyWorksheets.setMode(orgId, worksheet.policyKey, {
          mode,
          customText,
          groupId: parsedGroupId,
          locationId: parsedLocationId
        });
        if (!result.ok) {
          setModeError(result.error ?? "Could not update the policy mode.");
        } else {
          setGeneratedTexts(null);
          await load();
        }
      } catch {
        setModeError("Could not update the policy mode. Please try again.");
      } finally {
        setModeSaving(false);
      }
    },
    [orgId, parsedGroupId, parsedLocationId, load]
  );

  /** Swivl-staff: flip a swivl_staff_controlled item's visibility at this scope. */
  const handleSetItemVisibility = useCallback(
    async (worksheet: PolicyWorksheetViewDTO, itemKey: string, choice: PolicyItemVisibilityChoiceDTO): Promise<void> => {
      setVisibilitySaving(true);
      setVisibilityError(undefined);
      try {
        // Full replacement of this scope's own choices, mirroring answers.
        const own: Record<string, PolicyItemVisibilityChoiceDTO> = {};
        for (const [key, state] of Object.entries(worksheet.itemVisibility ?? {})) {
          if (state.inheritance === "none") {
            own[key] = state.value;
          }
        }
        own[itemKey] = choice;
        const result = await API.PolicyWorksheets.setItemVisibility(orgId, worksheet.policyKey, {
          groupId: parsedGroupId,
          locationId: parsedLocationId,
          itemVisibility: own
        });
        if (!result.ok) {
          setVisibilityError(result.error ?? "Could not update the item visibility.");
        } else {
          await load();
        }
      } catch {
        setVisibilityError("Could not update the item visibility. Please try again.");
      } finally {
        setVisibilitySaving(false);
      }
    },
    [orgId, parsedGroupId, parsedLocationId, load]
  );

  // Mode/visibility errors belong to the worksheet they happened on.
  useEffect(() => {
    setModeError(undefined);
    setVisibilityError(undefined);
  }, [activeKey]);

  // Live preview for the sticky staff card: re-derive the active worksheet's text
  // from the CURRENT draft (debounced), unsaved edits included, so the card always
  // shows what the bot would say right now.
  useEffect(() => {
    if (!Member.isSwivlStaff || !activeKey) return;
    const draft = drafts[activeKey] ?? {};
    const timer = setTimeout(() => {
      const worksheet = worksheets.find((entry) => entry.policyKey === activeKey);
      if (!worksheet) return;
      API.PolicyWorksheets.previewGeneratedText(orgId, activeKey, {
        groupId: parsedGroupId,
        locationId: parsedLocationId,
        answers: draft,
        revision: worksheet.revision
      })
        .then((result) =>
          setGeneratedTexts((previous) => ({
            ...(previous ?? {}),
            [activeKey]: result.ok ? (result.text ?? "") : (result.error ?? "Preview failed.")
          }))
        )
        .catch(() => setGeneratedTexts((previous) => ({ ...(previous ?? {}), [activeKey]: "Preview failed." })));
    }, 400);
    return () => clearTimeout(timer);
  }, [activeKey, drafts, worksheets, orgId, parsedGroupId, parsedLocationId]);

  if (loading) {
    return <div className="absolute inset-0 flex items-center justify-center text-grey-400">Loading policy worksheets…</div>;
  }
  if (errors["_load"]) {
    return <div className="absolute inset-0 flex items-center justify-center text-red-500">{errors["_load"]}</div>;
  }

  const activeDraft = activeWorksheet ? (drafts[activeWorksheet.policyKey] ?? {}) : {};
  const activeError = activeWorksheet ? errors[activeWorksheet.policyKey] : undefined;
  const activeEditing = activeWorksheet ? editingKeys[activeWorksheet.policyKey] === true : false;
  const topicSettings = activeWorksheet ? POLICY_TOPIC_SETTINGS[activeWorksheet.policyKey] : undefined;
  const topicSettingsVisible =
    topicSettings !== undefined &&
    activeWorksheet !== null &&
    (topicSettings.showIf === undefined || gateSatisfied(topicSettings.showIf, activeWorksheet, activeDraft));

  // Rendered above the items when position is "top" (e.g. gate-code's keypad
  // box), otherwise as a section below them. Kept in one place so both slots
  // share it.
  const topicSettingsBlock =
    activeWorksheet && topicSettings && topicSettingsVisible ? (
      <CortexTopicWorksheet
        key={activeWorksheet.policyKey}
        embedded={true}
        topicId={topicSettings.topicId}
        autoSave={topicSettings.autoSave}
        sectionEditing={topicSettings.sectionEditing}
      />
    ) : null;

  /** One row, plus the staff-visibility strip that belongs above it when there is one. */
  const renderItemRow = (worksheet: PolicyWorksheetViewDTO, item: WorksheetItemViewDTO, nested: boolean, hasRevealed = false): React.ReactElement => {
    const visibilityControl =
      item.visibility === "staff_only" || item.visibility === "swivl_staff_controlled" ? (
        <StaffVisibilityControl
          authored={item.visibility}
          state={worksheet.itemVisibility?.[item.itemKey]}
          saving={visibilitySaving}
          error={visibilityError}
          onSet={(choice) => handleSetItemVisibility(worksheet, item.itemKey, choice)}
        />
      ) : null;
    const row = (
      <WorksheetItemRow
        key={item.itemKey}
        worksheet={worksheet}
        item={item}
        isEditing={activeEditing}
        isOrgScope={isOrgScope}
        nested={nested}
        hasRevealed={hasRevealed}
        parentValue={parentAnswers[worksheet.policyKey]?.[item.itemKey]?.value}
        parentInheritance={parentAnswers[worksheet.policyKey]?.[item.itemKey]?.inheritance}
        draftValue={drafts[worksheet.policyKey]?.[item.itemKey]}
        onChange={(value) => handleAnswerChange(worksheet.policyKey, item.itemKey, value)}
        onRevert={() => handleRevertItem(worksheet.policyKey, item.itemKey)}
        onDiscard={() => handleDiscardItem(worksheet, item.itemKey)}
        onNavigate={(policyKey) => {
          setActiveKey(policyKey);
          setSearchParams({ policyKey }, { replace: true });
        }}
        urlLink={item.kind === "hint" ? buildUrlLink(item.linksToUrlKey, urlVars, urlsHref) : undefined}
        // staff_only: marker sits inline in the header beside the label.
        // swivl_staff_controlled keeps its strip above (it carries a show/hide toggle).
        headerStatus={item.visibility === "staff_only" ? visibilityControl : undefined}
      />
    );
    if (visibilityControl === null || item.visibility === "staff_only") {
      return row;
    }
    return (
      <div key={item.itemKey}>
        {visibilityControl}
        {row}
      </div>
    );
  };

  return (
    <div className="absolute inset-0 flex overflow-hidden">
      {/* Left - worksheet nav, sectioned by group label */}
      <aside className="w-60 shrink-0 overflow-y-auto border-r border-grey-400 bg-white">
        <div className="p-3">
          <SearchInput
            placeholder="Search..."
            value={railSearch}
            onChange={(event) => setRailSearch(event.target.value)}
            onClear={() => setRailSearch("")}
          />
        </div>
        {visibleGroups.map(([label, groupWorksheets]) => (
          <div key={label}>
            <div className="body-mono-alt px-4 pt-4 pb-1 text-grey-500 uppercase">{label}</div>
            <nav className="flex flex-col">
              {groupWorksheets.map((worksheet) => (
                <WorksheetNavRow
                  key={worksheet.policyKey}
                  worksheet={worksheet}
                  selected={worksheet.policyKey === activeKey}
                  dirty={(dirtyCounts[worksheet.policyKey] ?? 0) > 0}
                  onClick={() => {
                    setActiveKey(worksheet.policyKey);
                    setSearchParams({ policyKey: worksheet.policyKey }, { replace: true });
                  }}
                />
              ))}
            </nav>
          </div>
        ))}
        {Member.isSwivlStaff && (
          <div>
            <div className="body-mono-alt px-4 pt-4 pb-1 text-grey-500 uppercase">Swivl staff</div>
            <nav className="flex flex-col">
              <button
                type="button"
                onClick={() => {
                  setActiveKey(CUSTOM_INSTRUCTIONS_KEY);
                  setSearchParams({ policyKey: CUSTOM_INSTRUCTIONS_KEY }, { replace: true });
                }}
                className={filterClasses("SidebarLink cursor-pointer", activeKey === CUSTOM_INSTRUCTIONS_KEY && "selected")}
                style={{ paddingLeft: "1em", height: "38px" }}
              >
                <div className="chit" />
                <span className="min-w-0 flex-1 truncate text-left">Custom instructions</span>
              </button>
            </nav>
          </div>
        )}
      </aside>

      {/* Right - one worksheet per screen */}
      <main className="flex-1 overflow-y-auto">
        {activeKey === CUSTOM_INSTRUCTIONS_KEY && Member.isSwivlStaff ? (
          <div className="mx-auto max-w-[960px] p-6">
            <header className="mb-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-1 shrink-0 bg-grey-900" />
                  <h1 className="text-base font-semibold text-grey-900">Custom instructions</h1>
                </div>
                <p className="pl-3 text-base text-grey-600">
                  Free-form instructions for this business that become their own section of the bot's system prompt, after the platform rules. Swivl
                  staff only.
                </p>
              </div>
            </header>
            <CortexTopicWorksheet key={CUSTOM_INSTRUCTIONS_KEY} embedded={true} topicId={CUSTOM_INSTRUCTIONS_KEY} sectionEditing={true} />
          </div>
        ) : !activeWorksheet ? (
          <div className="flex h-full items-center justify-center text-grey-400">Select a worksheet</div>
        ) : (
          <div className={filterClasses("mx-auto p-6", Member.isSwivlStaff ? "max-w-[1360px]" : "max-w-[960px]")}>
            {/* The page header spans both columns, so the right column starts level with
                the first card rather than with the title. */}
            {/* Title block mirrors Details v3 (CortexTopicWorksheet): accent bar + text-base
                heading, description at text-base/grey-600. The group label is not repeated
                here — the left rail already shows it as that section's heading. */}
            <header className="mb-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-1 shrink-0 bg-grey-900" />
                  <h1 className="text-base font-semibold text-grey-900">{activeWorksheet.title}</h1>
                </div>
                {activeWorksheet.description && <p className="pl-3 text-base text-grey-600">{activeWorksheet.description}</p>}
              </div>
            </header>

            <div className="flex flex-col gap-4 min-[1400px]:flex-row">
              <div className="min-w-0 flex-1">
                {/* Cards are spaced by gap-4, matching Details v3's topic cards. */}
                <div className="flex flex-col gap-4">
                  <ProbeQuestions worksheet={activeWorksheet} />

                  {topicSettings?.position === "top" && topicSettingsBlock}

                  <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
                    {/* Card header, same grammar as Details v3's single-card topics (URLs,
                  Amenities & Services): title + caption on the left, and Edit —
                  swapping for Cancel + Save while editing — on the right. */}
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="heading-base-alt text-grey-900">{activeWorksheet.title}</h3>
                          <p className="body-sm mt-0.5 text-grey-600">Your answers become what the bot tells customers about this policy.</p>
                        </div>
                        {activeEditing ? (
                          <div className="flex shrink-0 items-center gap-2">
                            <Button
                              color={ButtonColor.WHITE}
                              size={ButtonSize.SMALL}
                              disabled={saving}
                              onClick={() => handleCancelEdits(activeWorksheet)}
                            >
                              Cancel
                            </Button>
                            <Button
                              color={ButtonColor.ORANGE}
                              size={ButtonSize.SMALL}
                              disabled={saving}
                              onClickAsync={() => handleSave(activeWorksheet)}
                            >
                              {saving ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingKeys((previous) => ({ ...previous, [activeWorksheet.policyKey]: true }))}
                            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
                          >
                            Edit
                            <BiEditAlt className="size-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-grey-200">
                      {/* Save error banner (same as Details v3's ScheduleCard / Admin.Page).
                    mt-5 with no bottom margin so the gap above it — divider to banner —
                    matches the gap below it: the rows wrapper's py-1 plus the first
                    row's py-4 also come to 20px. mx-5 lines its edges up with the rows. */}
                      {activeError && (
                        <p className="body-sm mx-5 mt-5 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{activeError}</p>
                      )}

                      <div className="divide-y divide-grey-200 px-5 py-1">
                        {(() => {
                          const visible = activeWorksheet.items.filter((item) => showIfSatisfied(item, activeWorksheet, activeDraft));
                          // A gated field belongs to the choice that reveals it, so it renders
                          // inside that row's group: the divider falls between groups rather
                          // than between a parent and its child, and the child's label indents.
                          const revealedBy = new Map<string, WorksheetItemViewDTO[]>();
                          for (const item of visible) {
                            if (item.showIf) {
                              revealedBy.set(item.showIf.itemKey, [...(revealedBy.get(item.showIf.itemKey) ?? []), item]);
                            }
                          }
                          // Cross-reference hints sit at the bottom of the card regardless of
                          // where they fall in the definition. They contribute nothing to the
                          // generated text, so their position in the item list is free.
                          const ordered = [...visible.filter((item) => item.kind !== "hint"), ...visible.filter((item) => item.kind === "hint")];
                          return ordered
                            .filter((item) => !item.showIf)
                            .map((item) => {
                              const revealed = revealedBy.get(item.itemKey) ?? [];
                              if (revealed.length === 0) {
                                return renderItemRow(activeWorksheet, item, false);
                              }
                              return (
                                // pb-4 closes the group, so a reveal group ends with the same
                                // bottom padding a standalone row has.
                                <div key={item.itemKey} className="pb-4">
                                  {renderItemRow(activeWorksheet, item, false, true)}
                                  {/* A revealed field can reveal its own ("Something else" → its note); it renders right after it. */}
                                  {revealed
                                    .flatMap((child) => [child, ...(revealedBy.get(child.itemKey) ?? [])])
                                    .map((child) => renderItemRow(activeWorksheet, child, true))}
                                </div>
                              );
                            });
                        })()}
                      </div>
                    </div>
                  </section>

                  {/* Policy-topic settings (location vars, own save path) — see POLICY_TOPIC_SETTINGS */}
                  {topicSettings?.position !== "top" && topicSettingsBlock}

                  {/* Staff-only: custom-text override controls */}
                  {Member.isSwivlStaff && (
                    <>
                      <StaffCustomTextPanel
                        key={`${activeWorksheet.policyKey}-${activeWorksheet.revision}`}
                        worksheet={activeWorksheet}
                        saving={modeSaving}
                        error={modeError}
                        onSaveCustomText={(customText) => handleSetMode(activeWorksheet, "custom", customText)}
                        onReturnToGenerate={() => handleSetMode(activeWorksheet, "generate")}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Right column: reference material that should stay in view while you edit
                the cards. Sticks to the top of the scroll container on wide screens and
                stacks underneath on narrow ones. */}
              {/* Staff-only column: operators would be left with an empty rail, so it
                  renders only when there is something in it. */}
              {Member.isSwivlStaff && (
                <aside className="w-full shrink-0 min-[1400px]:w-[260px] 2xl:w-[320px]">
                  <div className="min-[1400px]:sticky min-[1400px]:top-6">
                    <GeneratedTextCard
                      key={activeWorksheet.policyKey}
                      // Not `?? ""`: an absent key means "not fetched for this policy yet",
                      // and collapsing that to "" made the first load look like an edit that
                      // replaced the entire text, flashing the whole paragraph.
                      text={generatedTexts?.[activeWorksheet.policyKey]}
                    />
                  </div>
                </aside>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * The span of `next` that differs from `previous`, found by trimming the common
 * prefix and suffix. Returns null when the edit only removed text, since there is
 * then nothing left on screen to highlight.
 */
function changedRange(previous: string, next: string): { start: number; end: number } | null {
  const limit = Math.min(previous.length, next.length);
  let start = 0;
  while (start < limit && previous[start] === next[start]) {
    start += 1;
  }
  let fromEnd = 0;
  while (fromEnd < limit - start && previous[previous.length - 1 - fromEnd] === next[next.length - 1 - fromEnd]) {
    fromEnd += 1;
  }
  const end = next.length - fromEnd;
  return end > start ? { start, end } : null;
}

/**
 * Grow a changed range out to the sentence containing it, so the highlight reads as
 * "this sentence changed" rather than "these few characters changed" — editing a fee
 * amount should light up the whole sentence, not just the digits.
 */
function toSentenceBounds(text: string, start: number, end: number): { start: number; end: number } {
  const previousBreak = text.lastIndexOf(". ", Math.max(start - 1, 0));
  const nextBreak = text.indexOf(". ", Math.max(end - 1, 0));
  return {
    start: previousBreak === -1 ? 0 : previousBreak + 2,
    end: nextBreak === -1 ? text.length : Math.max(nextBreak + 1, end)
  };
}

/**
 * The span of `previous` that `next` no longer contains — the mirror of
 * `changedRange`, indexed into the old text so a removed sentence can be shown on its
 * way out. Null when nothing was removed.
 */
function removedRange(previous: string, next: string): { start: number; end: number } | null {
  const limit = Math.min(previous.length, next.length);
  let start = 0;
  while (start < limit && previous[start] === next[start]) {
    start += 1;
  }
  let fromEnd = 0;
  while (fromEnd < limit - start && previous[previous.length - 1 - fromEnd] === next[next.length - 1 - fromEnd]) {
    fromEnd += 1;
  }
  const end = previous.length - fromEnd;
  return end > start ? { start, end } : null;
}

/**
 * An added/changed sentence flashes in place; a removed one is shown struck through on
 * the old text for a beat before the shorter text takes over.
 */
type PreviewFlash =
  | { kind: "added"; start: number; end: number; seq: number }
  | { kind: "removed"; start: number; end: number; seq: number; text: string };

interface GeneratedTextCardProps {
  /** The derived policy text; undefined while the first preview is in flight. */
  text?: string;
}

/**
 * Sticky staff-only preview of the text the bot is actually served, rebuilt from the
 * current draft as the operator edits. Card grammar matches the rest of the page; the
 * body scrolls rather than growing, so a long policy can't push the card off screen.
 *
 * `text` is undefined until this policy's preview has arrived, which is what keeps a
 * fresh load from flashing as though everything changed; "" means it loaded and derived
 * nothing.
 *
 * When the text changes, the affected sentence flashes for ~2s; when one is removed it
 * shows struck through for ~1.5s before the shorter text takes over. The preview API
 * returns only the finished string with no per-item mapping, so the sentence is found
 * by diffing against the previous text — which also means the highlight shows what the
 * edit actually did to the output, not just which row was touched.
 */
function GeneratedTextCard({ text }: GeneratedTextCardProps): React.ReactElement {
  const reduceMotion = useReducedMotion();
  const previousText = useRef<string | undefined>(undefined);
  const flashSeq = useRef<number>(0);
  const [flash, setFlash] = useState<PreviewFlash | null>(null);

  useEffect(() => {
    const before = previousText.current;
    previousText.current = text;
    if (text === undefined || before === undefined || before === text) {
      return;
    }
    flashSeq.current += 1;
    const added = changedRange(before, text);
    if (added !== null) {
      setFlash({ kind: "added", ...toSentenceBounds(text, added.start, added.end), seq: flashSeq.current });
      const addedTimer = setTimeout(() => setFlash(null), 2100);
      return () => clearTimeout(addedTimer);
    }
    // Nothing was added, so this was a removal: hold the old text while the removed
    // sentence strikes through and fades, then let the shorter text take over.
    const removed = removedRange(before, text);
    if (removed === null) {
      setFlash(null);
      return;
    }
    setFlash({ kind: "removed", ...toSentenceBounds(before, removed.start, removed.end), seq: flashSeq.current, text: before });
    const removedTimer = setTimeout(() => setFlash(null), 1500);
    return () => clearTimeout(removedTimer);
  }, [text]);

  const body = ((): React.ReactNode => {
    if (text === undefined) {
      return "Loading…";
    }
    if (text === "") {
      return "(no text derived)";
    }
    if (flash === null) {
      return text;
    }
    // A removal renders against the OLD text, so the sentence is still there to fade.
    const source = flash.kind === "removed" ? flash.text : text;
    const marked = source.slice(flash.start, flash.end);
    return (
      <>
        {source.slice(0, flash.start)}
        {flash.kind === "removed" ? (
          // Strikethrough only, and no fade: it holds for a beat, then the shorter text
          // simply takes over. Nothing to animate, so no motion wrapper either.
          <span className="line-through">{marked}</span>
        ) : reduceMotion ? (
          <span className="bg-yellow-200">{marked}</span>
        ) : (
          <motion.span
            key={flash.seq}
            // Holds, then fades — "appeared and then went away" rather than a blink.
            animate={{ backgroundColor: ["rgba(255, 233, 127, 0.9)", "rgba(255, 233, 127, 0.9)", "rgba(255, 233, 127, 0)"] }}
            transition={{ duration: 2, times: [0, 0.7, 1], ease: "easeOut" }}
          >
            {marked}
          </motion.span>
        )}
        {source.slice(flash.end)}
      </>
    );
  })();

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="heading-base-alt text-grey-900">Generated Text</h3>
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
            swivl Support only
          </span>
        </div>
        <p className="body-sm mt-0.5 text-grey-600">What the bot is served. Updates as you edit, before you save.</p>
      </div>
      <div className="border-t border-grey-200 px-4 py-3">
        <pre className="heading-mono max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-grey-700">{body}</pre>
      </div>
    </section>
  );
}

interface ProbeQuestionsProps {
  worksheet: PolicyWorksheetViewDTO;
}

/**
 * Example customer questions the policy should answer, shown as a
 * closed-by-default disclosure above the worksheet's inputs to orient the
 * operator. Renders nothing when the worksheet has no probes.
 */
function ProbeQuestions({ worksheet }: ProbeQuestionsProps): React.ReactElement | null {
  const questions = worksheet.probeQuestions ?? [];
  const [open, setOpen] = useState<boolean>(false);
  const reduceMotion = useReducedMotion();
  const bodyId = `probe-questions-${worksheet.policyKey}`;

  if (questions.length === 0) {
    return null;
  }

  // A <details> element can't transition its own open/close — the browser drops the
  // content from flow instantly — so this is a button plus an animated region,
  // which lets the collapse animate as well as the expand.
  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((previous) => !previous)}
        className="heading-base-alt flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-grey-900"
      >
        What customers are asking the bot
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <span className="body-sm text-grey-600">
            {questions.length} {questions.length === 1 ? "example" : "examples"}
          </span>
          <TbChevronDown className={filterClasses("size-4 shrink-0 text-grey-600 transition-transform duration-200", open && "rotate-180")} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={bodyId}
            className="overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Plain stacked lines, aligned with the title. These are sentences to read,
                not chips to pick, and rounded-full pills read as interactive. */}
            <div className="flex flex-col gap-1.5 border-t border-grey-200 px-4 py-3">
              {questions.map((question) => (
                <p key={question} className="heading-mono text-grey-700">
                  {question}
                </p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

interface WorksheetNavRowProps {
  worksheet: PolicyWorksheetViewDTO;
  selected: boolean;
  dirty: boolean;
  onClick: () => void;
}

function WorksheetNavRow({ worksheet, selected, dirty, onClick }: WorksheetNavRowProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      // Shared left-nav styling (Components/SidebarLink/SidebarLink.scss): orange-50
      // hover and selected states, orange-500 text, and the 3px chit that slides in
      // on select. Kept as a button rather than a SidebarLink because switching
      // worksheets here is query-param state, not a route change.
      className={filterClasses("SidebarLink cursor-pointer", selected && "selected")}
      style={{ paddingLeft: "1em", height: "38px" }}
    >
      <div className="chit" />
      <span className="min-w-0 flex-1 truncate text-left">{worksheet.title}</span>
      {dirty && <span className="mr-2 size-2 shrink-0 rounded-full bg-orange-500" title="Unsaved changes" />}
    </button>
  );
}

interface StaffCustomTextPanelProps {
  worksheet: PolicyWorksheetViewDTO;
  saving: boolean;
  error?: string;
  onSaveCustomText: (customText: string) => void;
  onReturnToGenerate: () => void;
}

/**
 * Swivl-staff-only custom-text controls (design doc §3 / D6): flags when the
 * policy is served from hand-written custom text — at this scope or inherited
 * from an ancestor — and lets staff replace the generated text or return to
 * worksheet answers. Keyed by policyKey+revision upstream, so a successful
 * save (revision bump) remounts it closed and fresh.
 */
function StaffCustomTextPanel({ worksheet, saving, error, onSaveCustomText, onReturnToGenerate }: StaffCustomTextPanelProps): React.ReactElement {
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(worksheet.customText ?? "");

  const isCustom = worksheet.mode === "custom";

  const handleOpenEditor = (): void => {
    setDraft(worksheet.customText ?? "");
    setEditorOpen(true);
  };

  const handleReturnToGenerate = (): void => {
    if (!window.confirm("Return this policy to worksheet answers? The custom text at this scope will be removed.")) return;
    setEditorOpen(false);
    onReturnToGenerate();
  };

  /**
   * An empty field means "no custom text", so saving it clears the override rather
   * than storing a blank policy — the server rejects blank custom text outright, and
   * a blank override would leave the bot with nothing to say about this policy. With
   * nothing saved yet there is nothing to clear, so it just closes the editor.
   */
  const handleSaveClick = (): void => {
    if (draft.trim() !== "") {
      onSaveCustomText(draft);
      return;
    }
    if (isCustom) {
      handleReturnToGenerate();
      return;
    }
    setEditorOpen(false);
  };

  // Card grammar matches the policy card: title + caption + action in the header,
  // border-t, then the body. The staff-only marker uses the same purple pill the
  // restricted rows use rather than a parenthetical in every button label.
  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="heading-base-alt text-grey-900">Custom Policy Text</h3>
            <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
              swivl Support only
            </span>
          </div>
          <p className="body-sm mt-0.5 text-grey-600">
            Replaces the whole policy: the bot is served this text verbatim instead of the answers above.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {editorOpen ? (
            <>
              {/* Edit mode only, matching the policy rows: Revert appears while editing,
                  not at rest. */}
              {isCustom && (
                <button
                  type="button"
                  onClick={handleReturnToGenerate}
                  disabled={saving}
                  className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
                >
                  Return to Worksheet Answers
                  <GrRevert className="size-4" />
                </button>
              )}
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={saving} onClick={() => setEditorOpen(false)}>
                Cancel
              </Button>
              <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={saving} onClick={handleSaveClick}>
                {saving ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleOpenEditor}
              disabled={saving}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              Edit
              <BiEditAlt className="size-4" />
            </button>
          )}
        </div>
      </div>

      {/* One banner for both override states: yellow frame, grey-800 ink. Yellow because
          this is a situation to know about, not a failure — red is kept for genuine save
          errors (see below). grey-800 rather than yellow-800 is also a contrast fix:
          yellow-800 on yellow-50 measures 2.1:1, and even yellow-900, the darkest step,
          only reaches 3.5:1 — both under the 4.5:1 AA floor for body text. */}
      <div className="border-t border-grey-200">
        {isCustom || worksheet.inheritedCustom ? (
          <p className="body-sm mx-5 mt-5 rounded-sm border border-yellow-300 bg-yellow-50 px-3 py-2 text-grey-800">
            {isCustom
              ? "This text is overriding the worksheet. The answers above are not being served."
              : "Custom text from a parent scope is overriding this worksheet. The answers above are not being served."}
          </p>
        ) : null}

        <div className="px-5 py-4">
          {editorOpen ? (
            // A textarea is inline-block by default, so its container picks up a text
            // line box and leaves phantom space underneath.
            <TextArea
              label="Custom Policy Text"
              helperText="Served to the bot verbatim in place of the generated worksheet text at this scope."
              className="block"
              value={draft}
              rows={8}
              onChange={(event) => setDraft(event.target.value)}
            />
          ) : (
            <span className="body-sm break-words whitespace-pre-wrap text-grey-900">{worksheet.customText?.trim() || "—"}</span>
          )}
        </div>

        {error && <p className="body-sm mx-5 mb-4 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{error}</p>}
      </div>
    </section>
  );
}

/**
 * Mirrors the generator's gate semantics: a showIf-gated item is visible only
 * while its target's EFFECTIVE value (staged draft, else merged answer, else
 * the target's default) matches. Hidden values never contribute to generated
 * text server-side, so hiding here can't strand live policy content.
 */
// Gates chain like the generator's: a gate that is itself hidden hides what it reveals.
function showIfSatisfied(
  item: WorksheetItemViewDTO,
  worksheet: PolicyWorksheetViewDTO,
  draft: Record<string, string>,
  seen = new Set<string>()
): boolean {
  if (!item.showIf) {
    return true;
  }
  if (!gateSatisfied(item.showIf, worksheet, draft)) {
    return false;
  }
  const target = worksheet.items.find((candidate) => candidate.itemKey === item.showIf?.itemKey);
  if (!target || seen.has(item.itemKey)) {
    return true;
  }
  seen.add(item.itemKey);
  return showIfSatisfied(target, worksheet, draft, seen);
}

/** Effective-value gate check shared by item showIf and the topic-settings sections. */
function gateSatisfied(showIf: { itemKey: string; equals: string }, worksheet: PolicyWorksheetViewDTO, draft: Record<string, string>): boolean {
  const target = worksheet.items.find((candidate) => candidate.itemKey === showIf.itemKey);
  const effective = draft[showIf.itemKey] ?? worksheet.answers[showIf.itemKey]?.value ?? target?.defaultValue;
  return effective === showIf.equals;
}

interface StaffVisibilityControlProps {
  authored: "staff_only" | "swivl_staff_controlled";
  /** Effective choice + where it was declared (swivl_staff_controlled only). */
  state?: { value: PolicyItemVisibilityChoiceDTO; inheritance: WorksheetAnswerInheritanceDTO };
  saving: boolean;
  error?: string;
  onSet: (choice: PolicyItemVisibilityChoiceDTO) => void;
}

const VISIBILITY_SOURCE_LABEL: Record<WorksheetAnswerInheritanceDTO, string> = {
  none: "set at this scope",
  group: "from group",
  org: "from org",
  default: "default"
};

/**
 * Swivl-staff strip above a restricted item: staff_only items get a static
 * marker; swivl_staff_controlled items get the per-scope show/hide toggle
 * (2026-07-28-policy-item-visibility.md).
 */
function StaffVisibilityControl({ authored, state, saving, error, onSet }: StaffVisibilityControlProps): React.ReactElement {
  if (authored === "staff_only") {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">swivl Support only</span>
    );
  }

  const effective = state?.value ?? "staff_only";
  return (
    <div className="flex items-center gap-2 pt-3 text-xs">
      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700">swivl Support controlled</span>
      <span className="text-grey-400">
        {effective === "visible" ? "Operators see this input" : "Hidden from operators"} · {VISIBILITY_SOURCE_LABEL[state?.inheritance ?? "default"]}
      </span>
      <Button
        variant={ButtonVariant.GHOST}
        size={ButtonSize.SMALL}
        disabled={saving}
        onClick={() => onSet(effective === "visible" ? "staff_only" : "visible")}
      >
        {effective === "visible" ? "Hide from operators" : "Show to operators"}
      </Button>
      {error && <span className="text-red-600">{error}</span>}
    </div>
  );
}

/**
 * number_value inputs accept digits and at most one decimal point — anything
 * else is stripped as typed (server-side validateWorksheetAnswers is the
 * backstop; a trailing "." is allowed mid-typing and trimmed on blur).
 */
function sanitizeNumberValue(raw: string): string {
  const digitsAndDots = raw.replace(/[^0-9.]/g, "");
  const firstDot = digitsAndDots.indexOf(".");
  if (firstDot === -1) {
    return digitsAndDots;
  }
  return digitsAndDots.slice(0, firstDot + 1) + digitsAndDots.slice(firstDot + 1).replace(/\./g, "");
}

/**
 * Add or remove an optionKey from a multi_checkbox's comma-joined answer,
 * re-serializing in the definition's option order (not click order) so the
 * value the operator saves matches the order the generator emits sentences in.
 */
function toggleMultiCheckboxValue(item: WorksheetItemViewDTO, currentValue: string, optionKey: string, checked: boolean): string {
  const selected = new Set(currentValue.split(",").filter((token) => token !== ""));
  if (checked) {
    selected.add(optionKey);
  } else {
    selected.delete(optionKey);
  }
  return (item.options ?? [])
    .filter((option) => selected.has(option.optionKey))
    .map((option) => option.optionKey)
    .join(",");
}

/**
 * Read-mode rendering of an answer (Details v3 grammar): the value as plain
 * text, an em dash when nothing is set. Operators only see controls in edit mode.
 */
function readOnlyAnswerText(item: WorksheetItemViewDTO, displayValue: string): string {
  if (item.kind === "additive_checkbox" || item.kind === "boolean_checkbox") {
    return displayValue === "1" ? "Yes" : "No";
  }
  if (item.kind === "multi_checkbox") {
    const selected = new Set(displayValue.split(",").filter((token) => token !== ""));
    return (item.options ?? [])
      .filter((option) => selected.has(option.optionKey))
      .map((option) => option.label)
      .join(", ");
  }
  // radio / dropdown / parameterized_option all resolve through their options.
  if (item.options && item.options.length > 0) {
    return item.options.find((option) => option.optionKey === displayValue)?.label ?? "";
  }
  return displayValue.trim();
}

interface PolicyRowControlsProps {
  inheritance: LocationVarInheritance;
  overrideStaged: boolean;
  isOverride: boolean;
  /** A saved override whose revert is staged but not yet saved: nothing left to revert. */
  revertStaged: boolean;
  isOrgScope: boolean;
  /** How the parent scope resolves this item; undefined at org scope. */
  parentInheritance: LocationVarInheritance | undefined;
  isEditing: boolean;
  /** Human-readable value a revert would land on, shown in the tooltip. */
  revertsTo: string;
  onRevert: () => void;
  onDiscard: () => void;
}

/**
 * Row controls on the Details v3 model: the inheritance pill always, and Revert
 * only while editing a row that is actually customized. There is no Customize
 * gate — in edit mode, changing a field is what creates the override at this
 * scope (same as Details v3's CortexFeatureRow).
 */
function PolicyRowControls({
  inheritance,
  overrideStaged,
  isOverride,
  revertStaged,
  isOrgScope,
  parentInheritance,
  isEditing,
  revertsTo,
  onRevert,
  onDiscard
}: PolicyRowControlsProps): React.ReactElement {
  // Once a revert is staged the row reads like it was never customized — no
  // pill, no Revert — until the field is edited again.
  const customized = (isOverride || overrideStaged) && !revertStaged;
  const revertLabel = revertLabelFor({ isOrgScope, isOverride, inheritance, parentInheritance });

  const handleRevert = (): void => {
    if (!window.confirm(`${revertLabel}? This will discard the current customization for this field.`)) return;
    if (overrideStaged) onDiscard();
    else onRevert();
  };

  return (
    <>
      <CortexInheritancePill inheritance={customized ? LocationVarInheritance.NONE : inheritance} />
      {isEditing && customized && (
        <Tooltip content={`Reverts to: ${revertsTo}`}>
          <button
            type="button"
            onClick={handleRevert}
            className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-grey-900 hover:text-orange-500"
          >
            {revertLabel}
            <GrRevert className="size-4" />
          </button>
        </Tooltip>
      )}
    </>
  );
}

interface WorksheetItemRowProps {
  worksheet: PolicyWorksheetViewDTO;
  item: WorksheetItemViewDTO;
  /** True while the worksheet is in edit mode. */
  isEditing: boolean;
  /** No group or location selected, so there is no parent scope to revert to. */
  isOrgScope: boolean;
  /** This item is revealed by a choice above it, so it renders as that row's child. */
  nested?: boolean;
  /** This item reveals gated rows beneath it, so the group owns the spacing. */
  hasRevealed?: boolean;
  /** The parent scope's effective value for this item; undefined at org scope. */
  parentValue?: string;
  /** How the parent scope resolves this item; undefined at org scope. */
  parentInheritance?: LocationVarInheritance;
  /** The staged value at this scope; undefined = nothing set here (inherits). */
  draftValue?: string;
  onChange: (value: string) => void;
  onRevert: () => void;
  onDiscard: () => void;
  /** Switch the active worksheet — used by cross-reference hint links. */
  onNavigate: (policyKey: string) => void;
  /** Precomputed content for a `linksToUrlKey` hint — current value included. */
  urlLink?: { href: string; body: string; linkLabel: string };
  /** Rendered inline in the row header where the label sits (e.g. a restricted-visibility marker). */
  headerStatus?: React.ReactNode;
}

function WorksheetItemRow({
  worksheet,
  item,
  isEditing,
  isOrgScope,
  nested = false,
  hasRevealed = false,
  parentValue,
  parentInheritance,
  draftValue,
  onChange,
  onRevert,
  onDiscard,
  onNavigate,
  urlLink,
  headerStatus
}: WorksheetItemRowProps): React.ReactElement | null {
  // static_text and dynamic_reference contribute policy text only — nothing
  // operator-facing to render (D3). Hints render their guidance body.
  if (item.kind === "static_text" || item.kind === "dynamic_reference") {
    return null;
  }
  if (item.kind === "hint") {
    return urlLink ? (
      <PolicyHint body={urlLink.body} linkLabel={urlLink.linkLabel} linkHref={urlLink.href} />
    ) : (
      <PolicyHint body={item.body ?? ""} linkPolicyKey={item.linkPolicyKey} linkLabel={item.linkLabel} onNavigate={onNavigate} />
    );
  }

  const answer = worksheet.answers[item.itemKey];
  const savedOwnValue = answer?.inheritance === "none" ? answer.value : undefined;
  const isOverride = savedOwnValue !== undefined;
  const overrideStaged = draftValue !== savedOwnValue;
  // Edit mode is the only gate now — changing a field is what stages the override.
  const editable = isEditing;
  // A saved override with no draft means "Revert to Parent" is staged: show what the
  // row will inherit after save, not the override still sitting in `answer`.
  const revertStaged = isOverride && draftValue === undefined;
  const displayValue = draftValue ?? (revertStaged ? parentValue : answer?.value) ?? item.defaultValue ?? "";
  const controlName = `${worksheet.policyKey}-${item.itemKey}`;
  // A staged revert still has "none" saved, so show where the row will land instead.
  const rowInheritance = revertStaged ? (parentInheritance ?? LocationVarInheritance.DEFAULT) : INHERITANCE_TO_ENUM[answer?.inheritance ?? "default"];
  // Where a revert lands, which differs by case: discarding a staged edit returns
  // to this scope's SAVED state, while reverting a saved override falls through to
  // the parent scope (or the definition default at org scope).
  const revertsToValue =
    overrideStaged && !revertStaged ? (savedOwnValue ?? answer?.value ?? item.defaultValue ?? "") : (parentValue ?? item.defaultValue ?? "");
  const rowControls = (
    <PolicyRowControls
      inheritance={rowInheritance}
      overrideStaged={overrideStaged}
      isOverride={isOverride}
      revertStaged={revertStaged}
      isOrgScope={isOrgScope}
      parentInheritance={parentInheritance}
      isEditing={isEditing}
      revertsTo={readOnlyAnswerText(item, revertsToValue) || "—"}
      onRevert={onRevert}
      onDiscard={onDiscard}
    />
  );

  // Toggle: inline control, same Customize gate.
  if (item.kind === "additive_checkbox" || item.kind === "boolean_checkbox") {
    return (
      <PolicyToggleRow
        label={item.label}
        description={item.hint}
        isEditing={isEditing}
        on={displayValue === "1"}
        controls={rowControls}
        nested={nested}
        hasRevealed={hasRevealed}
        onToggle={(on) => onChange(on ? "1" : "0")}
      />
    );
  }

  // The staff note's hint describes what happens to whatever is typed, so it reads
  // after the field rather than above it, and it stays visible in read mode.
  const isStaffNote = item.visibility === "staff_only" && item.kind === "text_value";
  // mt-0.5 rather than mt-1: leading-snug already puts ~2px of half-leading above
  // the text, so 2px of margin renders as a 4px gap under the field.
  const hintBelow = isStaffNote && item.hint ? <p className="body-sm mt-0.5 text-grey-600">{item.hint}</p> : undefined;

  // Preview the built sentence (free-value kinds only) while editing. Bare "{value}"
  // templates are included too: they echo the field back, but that echo is the
  // confirmation of exactly what gets appended to the policy.
  const previewFooter =
    isEditing && (item.kind === "text_value" || item.kind === "number_value") && item.sentenceTemplate && displayValue.trim() !== "" ? (
      <p className="mt-2 rounded-sm border border-grey-200 bg-grey-50 px-3 py-2 text-grey-600">
        <span className="body-mono-alt uppercase">Preview</span> ·{" "}
        <span className="body-sm text-grey-900">{item.sentenceTemplate.replace("{value}", displayValue)}</span>
      </p>
    ) : undefined;

  return (
    <CortexRowShell
      label={item.label}
      titleStatus={headerStatus}
      // Descriptions are edit-mode only, matching Details v3. The staff note renders
      // its hint below the field instead (see hintBelow).
      description={isEditing && !isStaffNote ? item.hint : undefined}
      inheritance={rowInheritance}
      overrideStaged={overrideStaged}
      onCustomize={() => onChange(displayValue)}
      onRevert={onRevert}
      onDiscard={onDiscard}
      footer={
        previewFooter || hintBelow ? (
          <>
            {/* Hint first: it explains the field, so it sits directly under the input,
                with the generated-sentence preview after it. */}
            {hintBelow}
            {previewFooter}
          </>
        ) : undefined
      }
      controls={rowControls}
      layout="horizontal"
      nested={nested}
      hasRevealed={hasRevealed}
    >
      {!isEditing ? (
        <span className="body-sm break-words text-grey-900">{readOnlyAnswerText(item, displayValue) || "—"}</span>
      ) : item.kind === "text_value" ? (
        <Input
          name={controlName}
          disabled={!editable}
          value={displayValue}
          placeholder={item.placeholder ? "e.g. " + item.placeholder : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : item.kind === "number_value" ? (
        <Input
          name={controlName}
          disabled={!editable}
          value={displayValue}
          type="number"
          min={0}
          inputMode="decimal"
          placeholder={item.placeholder ? "e.g. " + item.placeholder : undefined}
          onChange={(event) => onChange(sanitizeNumberValue(event.target.value))}
          onBlur={() => {
            if (displayValue.endsWith(".")) {
              onChange(displayValue.slice(0, -1));
            }
          }}
        />
      ) : item.kind === "radio" ? (
        <PolicyRadio
          value={displayValue}
          disabled={!editable}
          options={(item.options ?? []).map((option) => ({ label: option.label, value: option.optionKey }))}
          onChange={(value) => {
            if (editable) onChange(value);
          }}
        />
      ) : item.kind === "multi_checkbox" ? (
        <div className="flex flex-col gap-2" role="group">
          {(() => {
            const selected = new Set(displayValue.split(",").filter((token) => token !== ""));
            return (item.options ?? []).map((option) => (
              <Checkbox
                key={option.optionKey}
                name={`${controlName}-${option.optionKey}`}
                label={option.label}
                disabled={!editable}
                checked={selected.has(option.optionKey)}
                classNames={{ label: "text-sm text-grey-700" }}
                onCheckedChange={(checked) => {
                  if (editable) onChange(toggleMultiCheckboxValue(item, displayValue, option.optionKey, checked === true));
                }}
              />
            ));
          })()}
        </div>
      ) : (
        <BasicSelect
          name={controlName}
          disabled={!editable}
          hideClear={true}
          selectedValues={displayValue ? [displayValue] : []}
          options={(item.options ?? []).map((option) => ({ label: option.label, value: option.optionKey }))}
          onChange={(selected) => {
            if (selected[0] !== undefined) {
              onChange(selected[0]);
            }
          }}
        />
      )}
    </CortexRowShell>
  );
}
